import { useStatus } from 'hooks/useStatus';
import { WorkOrder } from 'interfaces/WorkOrder';
import React from 'react';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router';

interface StyleProps {
  statusColor: string;
  statusShade: string;
}

const useStyles = createUseStyles({
  card: {
    boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.04)',
    marginTop: '10px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    borderRadius: '10px',
    padding: '15px',
    backgroundColor: 'white',
    position: 'relative',
    cursor: 'pointer',
    transition:
      'box-shadow 0.2s ease, transform 0.1s ease, background-color 0.3s ease, border-color 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.08)',
      transform: 'translateY(-2px)',
    },
    '&:active': {
      transform: 'translateY(0px)',
    },

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      backgroundColor: '#1a1a1a',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.3)',

      '&:hover': {
        boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.5)',
        transform: 'translateY(-2px)',
      },
    },
  },
  tag: {
    position: 'absolute',
    top: '15px',
    right: '15px',
    borderWidth: '0.5px',
    borderStyle: 'solid',
    borderRadius: '4px',
    padding: '3px 5px',
    height: 'fit-content',
    fontSize: '15px',
    fontWeight: 400,
    transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease',

    color: (props: StyleProps) => props.statusShade,
    borderColor: (props: StyleProps) => `${props.statusColor}10`,
    backgroundColor: (props: StyleProps) => `${props.statusColor}20`,

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: (props: StyleProps) => props.statusColor,
      borderColor: (props: StyleProps) => `${props.statusColor}40`,
      backgroundColor: (props: StyleProps) => `${props.statusColor}30`,
    },
  },
  primaryText: {
    margin: '10px 0',
    fontSize: '16px',
    fontWeight: 600,
    color: '#282828',
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#ffffff',
    },
  },
  secondaryText: {
    margin: 0,
    fontSize: '15px',
    fontWeight: 400,
    color: '#666666',
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#b3b3b3',
    },
  },
});

type WorkOrderCardProps = Pick<
  WorkOrder,
  'id' | 'workorderNumber' | 'title' | 'location' | 'status' | 'createdAt'
>;

const WorkOrderCard: React.FC<WorkOrderCardProps> = ({
  id,
  workorderNumber,
  title,
  location,
  status,
  createdAt,
}) => {
  const { color: statusColor, shade: statusShade, label: statusLabel } = useStatus(status);
  const classes = useStyles({ statusColor, statusShade });
  const history = useHistory();

  const parseWorkOrderNumber = (woNumber: WorkOrderCardProps['workorderNumber']): string => {
    if (!woNumber) return '';

    const parts = woNumber.split('-');
    const woNum = parts.length > 1 ? parts[1] : woNumber;
    return woNum ? `#${woNum}` : '';
  };

  const parseCreatedAt = (createdAtDate: WorkOrderCardProps['createdAt']): string => {
    if (!createdAtDate) return '';

    try {
      const dateObj = new Date(createdAtDate);
      if (isNaN(dateObj.getTime())) return '';

      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  return (
    <div
      className={classes.card}
      role="button"
      aria-label={`View details for ${workorderNumber} ${title}`}
      onClick={() => history.push(`/workorders/details/${id}`)}
    >
      <div className={classes.tag}>
        <p style={{ margin: 0 }}>{statusLabel}</p>
      </div>
      <p className={classes.secondaryText}>
        <span>{parseWorkOrderNumber(workorderNumber)}</span>
        <span> • </span>
        <span>{parseCreatedAt(createdAt)}</span>
      </p>
      <h2 className={classes.primaryText}>{title}</h2>
      {location?.address && (
        <p className={classes.secondaryText}>
          {location.address.split(',')[0] || location.address}
        </p>
      )}
    </div>
  );
};

export default WorkOrderCard;
